import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, Clock, FileVideo, GraduationCap, LockSimple, PlayCircle, Trash, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";

const STATUS_META = {
  not_started: ["Not started", "border-stone-300 bg-stone-100 text-stone-600"],
  waiting_for_content: ["Waiting for videos", "border-stone-300 bg-stone-100 text-stone-700"],
  locked: ["Locked", "border-stone-300 bg-stone-100 text-stone-400"],
  in_progress: ["In progress", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  exam_ready: ["Exam ready", "border-primary/30 bg-accent text-accent-foreground"],
  remediation_due: ["Remediation due", "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"],
  overdue: ["Overdue", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  failed: ["Exam failed", "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"],
  orientation_ready: ["Orientation ready", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
  complete: ["Complete", "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"],
};

const StatusBadge = ({ status, testid }) => {
  const [label, cls] = STATUS_META[status] || STATUS_META.not_started;
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`} data-testid={testid}>{label}</span>;
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
const fmtSize = (n) => (n ? `${(n / 1024 / 1024).toFixed(1)} MB` : "");

const videoEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export default function OnboardingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [moduleEdits, setModuleEdits] = useState({});
  const [videoTitles, setVideoTitles] = useState({});
  const [videoFiles, setVideoFiles] = useState({});
  const [videoLinks, setVideoLinks] = useState({});
  const [uploadPct, setUploadPct] = useState({});
  const [signatures, setSignatures] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data: moduleData } = await api.get("/onboarding/modules");
      setData(moduleData);
      const edits = {};
      for (const m of moduleData.modules || []) {
        edits[m.id] = {
          title: m.title,
          description: m.description || "",
          order: m.order,
          remediation_wrong_threshold: m.remediation_wrong_threshold,
          remediation_days: m.remediation_days,
        };
      }
      setModuleEdits(edits);
      if (isAdmin) {
        const { data: dash } = await api.get("/onboarding/dashboard");
        setDashboard(dash);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ modules: [] });
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  const saveModule = async (m) => {
    setBusy(`save-${m.id}`);
    try {
      await api.put(`/onboarding/modules/${m.id}`, moduleEdits[m.id]);
      toast.success("Onboarding module saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const uploadVideo = async (m) => {
    const file = videoFiles[m.id];
    if (!file) {
      toast.error("Choose a video file first");
      return;
    }
    setBusy(`upload-${m.id}`);
    setUploadPct((p) => ({ ...p, [m.id]: 0 }));
    try {
      const init = await api.post(`/onboarding/modules/${m.id}/videos/init`, {
        filename: file.name,
        title: videoTitles[m.id] || file.name.replace(/\.[^.]+$/, ""),
        content_type: file.type,
        size: file.size,
      });
      const uploadId = init.data.upload_id;
      const chunkSize = init.data.chunk_size || 4 * 1024 * 1024;
      const chunks = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < chunks; i += 1) {
        const blob = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
        await api.put(`/onboarding/uploads/${uploadId}/chunks/${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          timeout: 120000,
        });
        setUploadPct((p) => ({ ...p, [m.id]: Math.round(((i + 1) / chunks) * 100) }));
      }
      await api.post(`/onboarding/uploads/${uploadId}/complete`, {}, { timeout: 300000 });
      toast.success("Video uploaded");
      setVideoFiles((f) => ({ ...f, [m.id]: null }));
      setVideoTitles((t) => ({ ...t, [m.id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
      setUploadPct((p) => ({ ...p, [m.id]: undefined }));
    }
  };

  const linkVideo = async (m) => {
    const url = (videoLinks[m.id] || "").trim();
    if (!url) {
      toast.error("Paste a Vimeo or YouTube link first");
      return;
    }
    setBusy(`link-${m.id}`);
    try {
      await api.post(`/onboarding/modules/${m.id}/videos/link`, { url, title: videoTitles[m.id] || "" });
      toast.success("Video link added");
      setVideoLinks((x) => ({ ...x, [m.id]: "" }));
      setVideoTitles((t) => ({ ...t, [m.id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const deleteVideo = async (m, v) => {
    if (!window.confirm(`Remove "${v.title}" from ${m.title}? Employee signatures already captured remain on their records.`)) return;
    try {
      await api.delete(`/onboarding/modules/${m.id}/videos/${v.id}`);
      toast.success("Video removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const signVideo = async (m, v) => {
    const signature = signatures[v.id];
    if (!signature) {
      toast.error("Draw your signature first");
      return;
    }
    setBusy(`sign-${v.id}`);
    try {
      const { data: result } = await api.post(`/onboarding/modules/${m.id}/videos/${v.id}/sign`, { signature_png: signature });
      if (result.remediation_completed) toast.success("Remedial rewatch completed");
      else if (result.module_completed) toast.success(`"${m.title}" complete`);
      else toast.success("Video signed");
      setSignatures((s) => ({ ...s, [v.id]: null }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const modules = data.modules || [];
  const summary = data.summary || {};
  const exam = isAdmin ? data.exam : summary.exam;

  return (
    <div className="max-w-6xl" data-testid="onboarding-page">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Onboarding Training</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-stone-500">
            Video training, orientation reading, and the competency exam now live together on the Orientation page. This screen manages the video topics.
          </p>
        </div>
        {!isAdmin && summary.status && <StatusBadge status={summary.status} testid="onboarding-status" />}
      </div>

      {isAdmin ? (
        <AdminOnboarding
          modules={modules}
          dashboard={dashboard}
          moduleEdits={moduleEdits}
          setModuleEdits={setModuleEdits}
          videoTitles={videoTitles}
          setVideoTitles={setVideoTitles}
          videoFiles={videoFiles}
          setVideoFiles={setVideoFiles}
          uploadPct={uploadPct}
          busy={busy}
          saveModule={saveModule}
          uploadVideo={uploadVideo}
          linkVideo={linkVideo}
          videoLinks={videoLinks}
          setVideoLinks={setVideoLinks}
          deleteVideo={deleteVideo}
          exam={exam}
        />
      ) : (
        <div className="rounded-2xl border border-primary/30 bg-accent/40 p-8 text-center" data-testid="onboarding-moved-card">
          <GraduationCap size={40} className="mx-auto text-primary" />
          <p className="mt-3 font-heading text-2xl font-semibold text-stone-900">Everything lives in Orientation now</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-stone-600">
            Your training videos, the orientation reading parts, and the competency exam are all together in one place — with your progress tracked step by step.
          </p>
          <Link to="/orientation" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="onboarding-go-orientation">
            Go to Orientation
          </Link>
        </div>
      )}
    </div>
  );
}

function StaffOnboarding({ modules, summary, exam, signatures, setSignatures, busy, signVideo }) {
  const pct = summary.total_videos ? Math.round((summary.signed_videos / summary.total_videos) * 100) : 0;
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="onboarding-progress-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-stone-900">
              {summary.completed_modules || 0} of {summary.total_modules || 0} topics complete · {summary.signed_videos || 0} of {summary.total_videos || 0} videos signed
            </p>
            <p className="mt-1 text-xs text-stone-500">Finish one topic at a time. Your signature on each video is the proof of training.</p>
          </div>
          {summary.program_configured === false && (
            <span className="rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-3 py-1 text-xs font-semibold text-[#6f4208]" data-testid="onboarding-content-pending">
              The office is still uploading all training videos
            </span>
          )}
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-stone-100">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
        </div>
      </div>

      {(summary.remediations || []).length > 0 && (
        <div className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-5" data-testid="remediation-alert">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#8a5d14]"><WarningCircle size={18} /> Rewatch assigned after your competency exam</p>
          <div className="mt-3 space-y-2">
            {summary.remediations.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#B7791F]/20 bg-white p-3" data-testid={`remediation-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-stone-900">{r.title}</p>
                  <p className="text-xs text-stone-500">{r.wrong_count} missed · threshold {r.threshold} · due {fmtDate(r.due_at)}</p>
                </div>
                <StatusBadge status={r.overdue ? "overdue" : "remediation_due"} testid={`remediation-status-${r.id}`} />
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-stone-600">You may continue working until each listed due date. Re-watch the assigned topic below and sign each video again.</p>
        </div>
      )}

      <div className="space-y-4" data-testid="onboarding-modules">
        {modules.map((m, idx) => {
          const needsRemedialSignature = !!m.active_remediation;
          return (
            <section key={m.id} className={`rounded-lg border bg-white p-5 shadow-sm ${m.unlocked ? "border-stone-200" : "border-stone-200 opacity-70"}`} data-testid={`onboarding-module-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-heading text-lg font-semibold text-stone-900">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary">{idx + 1}</span>
                    {m.title}
                    <StatusBadge status={m.status} testid={`module-status-${m.id}`} />
                    {m.legacy_satisfied && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">Prior completion on record</span>}
                  </p>
                  {m.description && <p className="mt-1 text-sm text-stone-500">{m.description}</p>}
                  <p className="mt-1 text-xs text-stone-400">
                    Rewatch rule: {m.remediation_wrong_threshold}+ missed exam questions · {m.remediation_days} day{m.remediation_days === 1 ? "" : "s"} to complete
                  </p>
                </div>
                {!m.unlocked && <LockSimple size={20} className="text-stone-400" />}
              </div>

              <div className="mt-4 space-y-4">
                {(m.videos || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-4 text-sm text-stone-400" data-testid={`module-no-videos-${m.id}`}>Videos for this topic are being uploaded by the office.</p>
                )}
                {(m.videos || []).map((v, vi) => {
                  const signed = !!v.signed_at;
                  const remedialSigned = !!v.remediation_signed_at;
                  const needsSignature = m.unlocked && (!signed || (needsRemedialSignature && !remedialSigned));
                  return (
                    <div key={v.id} className="rounded-lg border border-stone-200 p-4" data-testid={`onboarding-video-${v.id}`}>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="flex items-center gap-2 text-sm font-medium text-stone-900">
                          <FileVideo size={18} className="text-primary" /> Video {vi + 1}: {v.title}
                        </p>
                        {needsRemedialSignature && !remedialSigned ? (
                          <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2 py-0.5 text-xs font-medium text-[#8a5d14]">Remedial rewatch required</span>
                        ) : signed ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-xs font-medium text-[#1e8449]"><CheckCircle size={13} weight="fill" /> Signed {fmtDate(v.signed_at)}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500"><Circle size={13} /> Not signed</span>
                        )}
                      </div>
                      {v.kind === "link" ? (
                        videoEmbedUrl(v.url) ? (
                          <iframe src={videoEmbedUrl(v.url)} title={v.title} className="aspect-video w-full rounded-lg border border-stone-200 bg-black" allowFullScreen data-testid={`video-player-${v.id}`} />
                        ) : (
                          <a href={v.url} target="_blank" rel="noreferrer" className="block rounded-lg border border-stone-200 bg-accent/30 p-4 text-sm font-medium text-primary hover:underline" data-testid={`video-player-${v.id}`}>
                            Open training video: {v.title}
                          </a>
                        )
                      ) : (
                        <video controls preload="metadata" className="aspect-video w-full rounded-lg border border-stone-200 bg-black" src={`${api.defaults.baseURL}/onboarding/videos/${v.id}`} data-testid={`video-player-${v.id}`} />
                      )}
                      {needsSignature && (
                        <div className="mt-4 rounded-lg border border-stone-200 bg-accent/30 p-4">
                          <p className="text-sm text-stone-700">I attest that I watched and understand this training video.</p>
                          <div className="mt-3">
                            <SignaturePad onChange={(sig) => setSignatures((s) => ({ ...s, [v.id]: sig }))} />
                          </div>
                          <div className="mt-3 flex justify-end">
                            <Button onClick={() => signVideo(m, v)} disabled={busy === `sign-${v.id}`} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`sign-video-${v.id}`}>
                              {busy === `sign-${v.id}` ? "Signing..." : needsRemedialSignature ? "Sign remedial rewatch" : "Sign video"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className={`rounded-lg border p-5 shadow-sm ${summary.orientation_ready ? "border-[#27AE60]/30 bg-[#27AE60]/5" : "border-stone-200 bg-white"}`} data-testid="competency-exam-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900"><GraduationCap size={20} /> Full competency exam</p>
            <p className="mt-1 text-sm text-stone-500">
              {summary.all_training_complete
                ? exam?.configured
                  ? `Your training is complete. Take the full exam now — passing score is ${exam.passing_score}%.`
                  : "Your training is complete. The office is preparing the exam questions."
                : "The exam unlocks after all seven training topics are complete."}
            </p>
            {exam?.passed && <p className="mt-1 text-xs font-medium text-[#1e8449]" data-testid="exam-passed-note">Exam passed {exam.score != null ? `(${exam.score}%)` : " (legacy record)"} {summary.remediations?.length ? "— finish the assigned rewatch above." : ""}</p>}
          </div>
          {summary.orientation_ready && summary.all_training_complete ? (
            <Link to="/orientation" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="go-orientation-button">
              Continue to Orientation
            </Link>
          ) : summary.exam_ready && exam?.token ? (
            <a href={`/train/${exam.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="take-competency-exam">
              Take Competency Exam
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-400" data-testid="exam-locked-note">
              <LockSimple size={16} /> Locked
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminOnboarding({ modules, dashboard, moduleEdits, setModuleEdits, videoTitles, setVideoTitles, videoFiles, setVideoFiles, videoLinks, setVideoLinks, uploadPct, busy, saveModule, uploadVideo, linkVideo, deleteVideo, exam }) {
  const summaryOrder = ["not_started", "waiting_for_content", "in_progress", "exam_ready", "remediation_due", "overdue", "failed", "orientation_ready"];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="onboarding-admin-summary">
        {summaryOrder.map((key) => (
          <div key={key} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`summary-${key}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">{STATUS_META[key][0]}</p>
            <p className="mt-2 font-heading text-3xl font-semibold text-stone-900">{dashboard?.summary?.[key] || 0}</p>
          </div>
        ))}
      </div>

      <section className="space-y-4" data-testid="onboarding-curriculum-admin">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-stone-900">Curriculum & videos</h2>
          <p className="mt-1 text-sm text-stone-500">
            Upload each topic's videos, then set the topic-specific rewatch trigger and grace period. Competency exam questions can be tagged to these topics in Training Center.
            {exam ? ` Current exam: ${exam.question_count} questions · passing ${exam.passing_score}%.` : ""}
          </p>
        </div>
        {modules.map((m) => {
          const edit = moduleEdits[m.id] || m;
          const pct = uploadPct[m.id];
          return (
            <div key={m.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`admin-module-${m.id}`}>
              <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-[80px_1fr]">
                    <div>
                      <Label>Order</Label>
                      <Input type="number" min="1" value={edit.order} onChange={(e) => setModuleEdits((x) => ({ ...x, [m.id]: { ...edit, order: e.target.value } }))} data-testid={`module-order-${m.id}`} />
                    </div>
                    <div>
                      <Label>Topic title</Label>
                      <Input value={edit.title} onChange={(e) => setModuleEdits((x) => ({ ...x, [m.id]: { ...edit, title: e.target.value } }))} data-testid={`module-title-${m.id}`} />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={edit.description} onChange={(e) => setModuleEdits((x) => ({ ...x, [m.id]: { ...edit, description: e.target.value } }))} data-testid={`module-description-${m.id}`} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>Rewatch trigger (missed questions)</Label>
                      <Input type="number" min="1" max="10" value={edit.remediation_wrong_threshold} onChange={(e) => setModuleEdits((x) => ({ ...x, [m.id]: { ...edit, remediation_wrong_threshold: e.target.value } }))} data-testid={`module-threshold-${m.id}`} />
                    </div>
                    <div>
                      <Label>Grace period (days)</Label>
                      <Input type="number" min="1" max="30" value={edit.remediation_days} onChange={(e) => setModuleEdits((x) => ({ ...x, [m.id]: { ...edit, remediation_days: e.target.value } }))} data-testid={`module-days-${m.id}`} />
                    </div>
                  </div>
                  <Button onClick={() => saveModule(m)} disabled={busy === `save-${m.id}`} variant="outline" className="border-stone-300" data-testid={`module-save-${m.id}`}>
                    {busy === `save-${m.id}` ? "Saving..." : "Save settings"}
                  </Button>
                </div>
                <div className="rounded-lg border border-stone-200 bg-accent/30 p-4">
                  <Label>Upload video</Label>
                  <Input value={videoTitles[m.id] || ""} onChange={(e) => setVideoTitles((x) => ({ ...x, [m.id]: e.target.value }))} placeholder="Video title" className="mt-2 bg-white" data-testid={`video-title-${m.id}`} />
                  <input type="file" accept="video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v" onChange={(e) => setVideoFiles((x) => ({ ...x, [m.id]: e.target.files?.[0] || null }))}
                    className="mt-3 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-accent file:px-3 file:py-1 file:text-xs file:font-medium" data-testid={`video-file-${m.id}`} />
                  {pct !== undefined && <p className="mt-2 text-xs font-medium text-primary" data-testid={`upload-progress-${m.id}`}>Uploading… {pct}%</p>}
                  <Button onClick={() => uploadVideo(m)} disabled={busy === `upload-${m.id}`} className="mt-3 w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`video-upload-${m.id}`}>
                    <UploadSimple size={15} className="mr-1.5" /> {busy === `upload-${m.id}` ? "Uploading..." : "Upload video"}
                  </Button>
                  <div className="mt-3 border-t border-stone-200 pt-3">
                    <Label>…or link a hosted video</Label>
                    <Input value={videoLinks[m.id] || ""} onChange={(e) => setVideoLinks((x) => ({ ...x, [m.id]: e.target.value }))} placeholder="https://vimeo.com/123456789" className="mt-2 bg-white" data-testid={`video-link-${m.id}`} />
                    <Button onClick={() => linkVideo(m)} disabled={busy === `link-${m.id}`} variant="outline" className="mt-2 w-full border-stone-300" data-testid={`video-link-add-${m.id}`}>
                      {busy === `link-${m.id}` ? "Adding..." : "Add video link"}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2" data-testid={`module-videos-${m.id}`}>
                {(m.videos || []).length === 0 && <p className="rounded-lg border border-dashed border-stone-300 p-3 text-sm text-stone-400">No videos uploaded yet.</p>}
                {(m.videos || []).map((v, i) => (
                  <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 p-3" data-testid={`admin-video-${v.id}`}>
                    <p className="flex items-center gap-2 text-sm font-medium text-stone-800"><PlayCircle size={17} className="text-primary" /> {i + 1}. {v.title} <span className="text-xs font-normal text-stone-400">{v.kind === "link" ? "Link" : fmtSize(v.size)}</span></p>
                    <div className="flex items-center gap-2">
                      <a href={v.kind === "link" ? v.url : `${api.defaults.baseURL}/onboarding/videos/${v.id}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline" data-testid={`video-open-${v.id}`}>Open</a>
                      <button onClick={() => deleteVideo(m, v)} className="text-stone-400 hover:text-[#C0392B]" data-testid={`video-delete-${v.id}`}><Trash size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <section data-testid="onboarding-staff-dashboard">
        <h2 className="font-heading text-2xl font-semibold text-stone-900">Staff onboarding status</h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Training</th>
                <th className="px-4 py-3">Exam</th>
                <th className="px-4 py-3">Remediation</th>
                <th className="px-4 py-3">Orientation</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard?.rows || []).map((r, i) => (
                <tr key={r.id} className="border-b border-stone-100 last:border-0" data-testid={`onboarding-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/team/${r.id}`} className="font-medium text-stone-900 hover:text-primary hover:underline" data-testid={`onboarding-employee-${i}`}>{r.name || r.email}</Link>
                    <p className="text-xs text-stone-400">{r.employee_id || r.email}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} testid={`row-status-${r.id}`} /></td>
                  <td className="px-4 py-3 text-stone-600" data-testid={`row-training-${r.id}`}>{r.completed_modules}/{r.total_modules} topics · {r.signed_videos}/{r.total_videos} videos</td>
                  <td className="px-4 py-3" data-testid={`row-exam-${r.id}`}>{r.exam?.passed ? <span className="font-medium text-[#1e8449]">{r.exam.score != null ? `${r.exam.score}%` : "Legacy pass"}</span> : <span className="text-stone-400">Not passed</span>}</td>
                  <td className="px-4 py-3" data-testid={`row-remediation-${r.id}`}>
                    {(r.remediations || []).length ? r.remediations.map((x) => <p key={x.id} className="text-xs text-[#8a5d14]">{x.title} · due {fmtDate(x.due_at)}</p>) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-4 py-3" data-testid={`row-orientation-${r.id}`}>{r.orientation_done ? <span className="text-[#1e8449]">Signed</span> : <span className="text-stone-400">Pending</span>}</td>
                </tr>
              ))}
              {(dashboard?.rows || []).length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-stone-400">No active staff found.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
